import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DriverWorkflow } from '@symfa-inc/providence-types';
import { HttpService } from '@core/services';
import {
  DriverManagementHttpService,
  EquipmentHttpService,
  PermittingHttpService,
} from '@core/services/http';
import { PermittingActions } from '@store/actions';
import { PermittingState } from '@store/reducers/permitting.reducer';
import { PermittingSelectors } from '@store/selectors';

type PrefetchTabsMap = Partial<
  Record<
    keyof PermittingState['tabData'],
    Array<keyof PermittingState['tabData']>
  >
>;

type Tab = keyof PermittingState['tabData'];

const assertNever = (value: never): never => {
  throw new Error(`Unexpected tab value for the prefetch: ${value}`);
};

export const usePermittingTabPrefetch = (
  currentTab: Tab,
  projectBundleID: string,
) => {
  const dispatch = useDispatch();

  const workflowLoaded = useSelector(
    PermittingSelectors.isTabDataLoaded('workflow'),
  );
  const summaryLoaded = useSelector(
    PermittingSelectors.isTabDataLoaded('summary'),
  );
  const jurisdictionAuditLoaded = useSelector(
    PermittingSelectors.isTabDataLoaded('jurisdictionAudit'),
  );
  const contactsLoaded = useSelector(
    PermittingSelectors.isTabDataLoaded('contacts'),
  );
  const sofLoaded = useSelector(PermittingSelectors.isTabDataLoaded('sof'));
  const financialLoaded = useSelector(
    PermittingSelectors.isTabDataLoaded('financial'),
  );

  useEffect(() => {
    const prefetchDelay = 1000;

    const prefetchMap: PrefetchTabsMap = {
      summary: [
        'workflow',
        'jurisdictionAudit',
        'contacts',
        'sof',
        'financial',
      ],
      workflow: ['jurisdictionAudit', 'contacts', 'sof', 'financial'],
      jurisdictionAudit: ['workflow', 'contacts', 'sof', 'financial'],
      contacts: ['workflow', 'jurisdictionAudit', 'sof', 'financial'],
      sof: ['workflow', 'jurisdictionAudit', 'contacts', 'financial'],
      financial: ['workflow', 'jurisdictionAudit', 'contacts', 'sof'],
    };

    const loadingStates = {
      workflow: workflowLoaded,
      summary: summaryLoaded,
      jurisdictionAudit: jurisdictionAuditLoaded,
      contacts: contactsLoaded,
      sof: sofLoaded,
      financial: financialLoaded,
    };

    const timer = setTimeout(() => {
      prefetchMap[currentTab]?.forEach(async (tabName: Tab) => {
        if (!loadingStates[tabName as keyof typeof loadingStates]) {
          dispatch(
            PermittingActions.setTabLoading.done({
              tabName,
              isLoading: true,
            }),
          );

          switch (tabName) {
            case 'workflow':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingWorkflowData(projectBundleID, {
                silent: true,
              });

              break;

            case 'summary':
              break;
            case 'contacts':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingContactsData(projectBundleID, {
                silent: true,
              });

              break;

            case 'jurisdictionAudit':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingJurisdictionAuditData(projectBundleID, {
                silent: true,
              });

              break;

            case 'sof':
              await HttpService.getHttpRequests(
                EquipmentHttpService,
              ).getEquipmentTypes(undefined, {
                silent: true,
              });

              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingSofData(projectBundleID, {
                silent: true,
              });

              break;

            case 'financial':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingFinancialData(projectBundleID, {
                silent: true,
              });

              await HttpService.getHttpRequests(
                DriverManagementHttpService,
              ).getDriversListByWorkflow(DriverWorkflow.Permitting, {
                silent: true,
              });

              break;

            default:
              assertNever(tabName);
          }
        }
      });
    }, prefetchDelay);

    return () => clearTimeout(timer);
  }, [
    currentTab,
    summaryLoaded,
    workflowLoaded,
    jurisdictionAuditLoaded,
    contactsLoaded,
    sofLoaded,
    financialLoaded,
  ]);
};
